import { useEffect, useRef } from "react";
import { OrgRoleIds } from "models/Role.model";
import { useRequestNgcUserVerificationMutation } from "server/ngc/mutations";
import { useNgcMeQuery } from "server/ngc/queries";

export type NgcApiKeyAccessStatus =
  | "hidden"
  | "blocked"
  | "checking"
  | "available"
  | "unavailable";

interface UseNgcApiKeyAccessOptions {
  enabled: boolean;
  email?: string | null;
  activeOrgRole?: OrgRoleIds;
  activeOrgHasNgcOrgIdWithoutAIEnablement?: boolean;
  shouldBlockDueToOtherAIEnabledOrg?: boolean;
}

export const isNvidiaEmail = (email?: string | null) =>
  !!email?.trim().toLowerCase().endsWith("@nvidia.com");

export const canCheckNgcApiKeyAccess = ({
  enabled,
  email,
  activeOrgRole,
  activeOrgHasNgcOrgIdWithoutAIEnablement,
  shouldBlockDueToOtherAIEnabledOrg,
}: UseNgcApiKeyAccessOptions) =>
  enabled &&
  !!email?.trim() &&
  activeOrgRole === OrgRoleIds.Owner &&
  !activeOrgHasNgcOrgIdWithoutAIEnablement &&
  !shouldBlockDueToOtherAIEnabledOrg &&
  !isNvidiaEmail(email);

export const useNgcApiKeyAccess = ({
  enabled,
  email,
  activeOrgRole,
  activeOrgHasNgcOrgIdWithoutAIEnablement,
  shouldBlockDueToOtherAIEnabledOrg,
}: UseNgcApiKeyAccessOptions) => {
  const shouldCheckAccess = canCheckNgcApiKeyAccess({
    enabled,
    email,
    activeOrgRole,
    activeOrgHasNgcOrgIdWithoutAIEnablement,
    shouldBlockDueToOtherAIEnabledOrg,
  });
  const normalizedEmail = email?.trim().toLowerCase() ?? "";
  const {
    data: me,
    isInitialLoading,
    isError,
  } = useNgcMeQuery(normalizedEmail, shouldCheckAccess);
  const {
    mutate: requestVerification,
    isLoading: isVerificationLoading,
    isError: isVerificationError,
  } = useRequestNgcUserVerificationMutation(normalizedEmail);
  const verificationRequestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (
      !shouldCheckAccess ||
      !me ||
      me.user?.blocked ||
      me.user?.verified !== undefined ||
      isVerificationLoading ||
      verificationRequestedFor.current === normalizedEmail
    ) {
      return;
    }

    verificationRequestedFor.current = normalizedEmail;
    requestVerification();
  }, [
    isVerificationLoading,
    me,
    normalizedEmail,
    requestVerification,
    shouldCheckAccess,
  ]);

  if (!shouldCheckAccess) return "hidden";
  if (isInitialLoading || isVerificationLoading) return "checking";
  if (isError || isVerificationError) return "unavailable";
  if (me?.user?.blocked) return "blocked";
  if (me?.user?.verified === true) return "available";
  if (me?.user?.verified === false) return "unavailable";
  return "checking";
};
